import { Type } from "nanium/objects";

export const gatheringCostOptions = {
  free: 'kostenlos',
  paid: 'kostenpflichtig'
};

export interface GatheringCategoryDefinition {
  /** Stable key used to reference this category in code (e.g. 'culture'). */
  key: string;
  /** Display name (matches facility-categories.ts where applicable). */
  name: string;
  /** Brand colour for the badge/chip. */
  color: string;
  /** Material Symbols icon name (matches facility-categories.ts). */
  materialSymbol: string;
  /** Subtype flags belonging to this category. */
  subtypes: { key: keyof GatheringFeatures; label: string }[];
}

/**
 * Top-level event categories. The task spec says at least one and at most
 * two of these may be active for a single gathering — enforced in the
 * editor UI. A category counts as "active" when any of its subtype flags
 * is true on the GatheringFeatures.
 */
export const gatheringCategories: GatheringCategoryDefinition[] = [
  {
    key: 'culture',
    name: 'Kultur & Kreativität',
    color: '#FF9F45',
    materialSymbol: 'theater_comedy',
    subtypes: [
      { key: 'catCultureConcert',    label: 'Konzert / Festival' },
      { key: 'catCultureMusical',    label: 'Musical / Show' },
      { key: 'catCultureTheater',    label: 'Theater / Performance' },
      { key: 'catCulturePoetry',     label: 'Poetry Slam / Lesung' },
      { key: 'catCultureExhibition', label: 'Ausstellung / Vernissage' },
      { key: 'catCultureWorkshop',   label: 'Kreativworkshop' },
      { key: 'catCultureOther',      label: 'Anderes "Kultur & Kreativität"' }
    ]
  },
  {
    key: 'sport',
    name: 'Sport & Bewegung',
    color: '#4ECDC4',
    materialSymbol: 'sports_soccer',
    subtypes: [
      { key: 'catSportTraining',   label: 'Training / offenes Sportangebot' },
      { key: 'catSportTournament', label: 'Turnier / Wettkampf' },
      { key: 'catSportCourse',     label: 'Sportkurs' },
      { key: 'catSportExam',       label: 'Prüfung' },
      { key: 'catSportOther',      label: 'Anderes "Sport & Bewegung"' }
    ]
  },
  {
    key: 'gaming',
    name: 'Gaming & Medien',
    color: '#B36BE6',
    materialSymbol: 'sports_esports',
    subtypes: [
      { key: 'catGamingFreeGaming', label: 'offenes Zocken' },
      { key: 'catGamingTournament', label: 'Gaming-Turnier / E-Sport' },
      { key: 'catGamingProduction', label: 'Film- / Medienproduktion' },
      { key: 'catGamingWorkshop',   label: 'Mediaworkshop' },
      { key: 'catGamingOther',      label: 'Anderes "Gaming & Media"' }
    ]
  },
  {
    key: 'education',
    name: 'Bildung & Lernen',
    color: '#FFD93D',
    materialSymbol: 'cast_for_education',
    subtypes: [
      { key: 'catEducationInfo',       label: 'Infoveranstaltung' },
      { key: 'catEducationCounseling', label: 'Beratung / Sprechstunde' },
      { key: 'catEducationHomework',   label: 'Hausaufgabenhilfe / offenes Lernen' },
      { key: 'catEducationCoaching',   label: 'Coaching' },
      { key: 'catEducationOther',      label: 'Anderes "Bildung & Lernen"' }
    ]
  },
  {
    key: 'outdoor',
    name: 'Outdoor Area',
    color: '#37FF8B',
    materialSymbol: 'nature_people',
    subtypes: [
      { key: 'catOutdoorNature',    label: 'Naturaktion / Umweltprojekt' },
      { key: 'catOutdoorExcursion', label: 'Ausflug / Exkursion' },
      { key: 'catOutdoorCamp',      label: 'Camp' },
      { key: 'catOutdoorOther',     label: 'Anderes "Outdoor Area"' }
    ]
  },
  {
    key: 'engagement',
    name: 'Engagement & Mitmachen',
    color: '#FFB3D1',
    materialSymbol: 'diversity_1',
    subtypes: [
      { key: 'catEngagementAction',    label: 'Aktionstag / Kampagne' },
      { key: 'catEngagementVolunteer', label: 'Ehrenamt' },
      { key: 'catEngagementCommittee', label: 'Gremium / Sitzung' },
      { key: 'catEngagementGroup',     label: 'Gruppentreffen / Jugendgruppe' },
      { key: 'catEngagementOther',     label: 'Anderes "Engagement & Mitmachen"' }
    ]
  }
];

export const gatheringAgeOptions: { key: keyof GatheringFeatures; label: string }[] = [
  { key: 'ageBelow12',       label: 'bis 12 Jahre' },
  { key: 'age12To16',        label: '12 - 16 Jahre' },
  { key: 'age16To18',        label: '16 - 18 Jahre' },
  { key: 'ageAbove18',       label: 'ab 18 Jahre' },
  { key: 'ageAll',           label: 'für alle Altersgruppen' },
  { key: 'ageFamilyFriendly',label: 'familiengeeignet' }
];

export const gatheringEventTypeOptions: { key: keyof GatheringFeatures; label: string }[] = [
  { key: 'typeOffline', label: 'offline' },
  { key: 'typeOnline',  label: 'online' },
  { key: 'typeHybrid',  label: 'hybrid' }
];

export class GatheringFeatures {
  // Altersausprägung (Mehrfachauswahl möglich)
  @Type(Boolean) ageBelow12?: boolean;
  @Type(Boolean) age12To16?: boolean;
  @Type(Boolean) age16To18?: boolean;
  @Type(Boolean) ageAbove18?: boolean;
  @Type(Boolean) ageAll?: boolean;
  @Type(Boolean) ageFamilyFriendly?: boolean;

  // Kosten (keine Mehrfachauswahl): 'free' | 'paid'
  @Type(String) cost?: string;

  // Art der Veranstaltung (Mehrfachauswahl möglich)
  @Type(Boolean) typeOffline?: boolean;
  @Type(Boolean) typeOnline?: boolean;
  @Type(Boolean) typeHybrid?: boolean;

  // Kultur & Kreativität
  @Type(Boolean) catCultureConcert?: boolean;
  @Type(Boolean) catCultureMusical?: boolean;
  @Type(Boolean) catCultureTheater?: boolean;
  @Type(Boolean) catCulturePoetry?: boolean;
  @Type(Boolean) catCultureExhibition?: boolean;
  @Type(Boolean) catCultureWorkshop?: boolean;
  @Type(Boolean) catCultureOther?: boolean;

  // Sport & Bewegung
  @Type(Boolean) catSportTraining?: boolean;
  @Type(Boolean) catSportTournament?: boolean;
  @Type(Boolean) catSportCourse?: boolean;
  @Type(Boolean) catSportExam?: boolean;
  @Type(Boolean) catSportOther?: boolean;

  // Gaming & Media
  @Type(Boolean) catGamingFreeGaming?: boolean;
  @Type(Boolean) catGamingTournament?: boolean;
  @Type(Boolean) catGamingProduction?: boolean;
  @Type(Boolean) catGamingWorkshop?: boolean;
  @Type(Boolean) catGamingOther?: boolean;

  // Bildung & Lernen
  @Type(Boolean) catEducationInfo?: boolean;
  @Type(Boolean) catEducationCounseling?: boolean;
  @Type(Boolean) catEducationHomework?: boolean;
  @Type(Boolean) catEducationCoaching?: boolean;
  @Type(Boolean) catEducationOther?: boolean;

  // Outdoor Area
  @Type(Boolean) catOutdoorNature?: boolean;
  @Type(Boolean) catOutdoorExcursion?: boolean;
  @Type(Boolean) catOutdoorCamp?: boolean;
  @Type(Boolean) catOutdoorOther?: boolean;

  // Engagement & Mitmachen
  @Type(Boolean) catEngagementAction?: boolean;
  @Type(Boolean) catEngagementVolunteer?: boolean;
  @Type(Boolean) catEngagementCommittee?: boolean;
  @Type(Boolean) catEngagementGroup?: boolean;
  @Type(Boolean) catEngagementOther?: boolean;
}

export function getActiveGatheringCategoryKeys(features: GatheringFeatures | undefined): string[] {
  if (!features) return [];
  const f = features as { [k: string]: any };
  return gatheringCategories
    .filter(cat => cat.subtypes.some(st => f[st.key] === true))
    .map(cat => cat.key);
}
